import type {
  Location,
  ObservationsMapParameterDisplayGroup,
  ObservationsMapParameterMapLayerConfiguration,
  ParametersObservedMapLayer,
  ParameterTimeSeriesConfiguration,
} from "@/lib/api";
import { LayerConfig } from "@/lib/map-layers/layer-state-machine";

import { Parameters_ObservedMapLayer } from "./layer";
import { ObservedStationMarkersLayer } from "./observed-station-markers-layer";

/** An observed map layer joined with the widget config that references it. */
export type ConfiguredMapLayer = {
  layer: ParametersObservedMapLayer;
  widgetConfig: ObservationsMapParameterMapLayerConfiguration;
};

export type BuildObservationsMapLayerConfigsParams = {
  /** Observed map (tile) layers, in widget-config order. */
  configuredLayers: ConfiguredMapLayer[];
  /** Whether the widget has any station time-series (drives the marker layer). */
  hasStationLayer: boolean;
  /**
   * Whether the station layer may be added yet. On the dashboard this waits for
   * the published-locations query (when filtering); in the CMS preview there is
   * no such query, so it is always ready.
   */
  stationLayerReady: boolean;
  displayGroups: ObservationsMapParameterDisplayGroup[];
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  resolveParameterLabel: (parameterId: string) => string | undefined;
  showAllLocations: boolean;
  weatherStationsEnabledByDefault: boolean;
  /**
   * Dashboard locations used to filter/prioritize stations. Absent (CMS preview)
   * ⇒ all stations in default order.
   */
  dashboardLocations?: Location[];
};

/**
 * Build the ordered `LayerConfig[]` for an observations map: the observed tile
 * layers (in config order) followed by the station-markers layer when the
 * widget has time-series and the station layer is ready.
 *
 * Pure and container-agnostic: the dashboard smart container and the CMS preview
 * both call this. Station filtering/priority is driven entirely by the optional
 * `dashboardLocations` input, so the CMS (no published dashboard) shows all
 * stations in default order.
 */
export function buildObservationsMapLayerConfigs({
  configuredLayers,
  hasStationLayer,
  stationLayerReady,
  displayGroups,
  timeSeriesConfigurations,
  resolveParameterLabel,
  showAllLocations,
  weatherStationsEnabledByDefault,
  dashboardLocations,
}: BuildObservationsMapLayerConfigsParams): LayerConfig[] {
  const configs: LayerConfig[] = configuredLayers.map(({ layer, widgetConfig }) => ({
    layer: new Parameters_ObservedMapLayer(layer, widgetConfig),
    isEnabledInitially: widgetConfig.enabled_by_default,
  }));

  if (hasStationLayer && stationLayerReady) {
    configs.push({
      layer: new ObservedStationMarkersLayer(
        displayGroups,
        timeSeriesConfigurations,
        resolveParameterLabel,
        {
          showAllLocations,
          dashboardLocations,
        },
      ),
      isEnabledInitially: weatherStationsEnabledByDefault,
    });
  }

  return configs;
}
